import { useMsal } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  SilentRequest,
  AuthenticationResult,
  AccountInfo,
} from "@azure/msal-browser";
import { useEffect, useRef } from "react";

// Updated callback type to include both token and account
type OnTokenRefreshedCallback = (
  token: string | null,
  account: AccountInfo | null
) => void;

interface TokenResponse {
  token: string | null;
  account: AccountInfo | null;
}

const useSilentIdTokenRefresh = (
  onTokenRefreshed?: OnTokenRefreshedCallback
): ((forceRefresh?: boolean) => Promise<TokenResponse>) => {
  const { instance, accounts } = useMsal();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const getIdTokenSilently = async (
    forceRefresh: boolean = false
  ): Promise<TokenResponse> => {
    if (!accounts || accounts.length === 0) {
      console.warn("No user account found for silent token acquisition.");
      const result = { token: null, account: null };
      onTokenRefreshed?.(result.token, result.account);
      return result;
    }

    const silentRequest: SilentRequest = {
      account: accounts[0] as AccountInfo,
      scopes: ["openid", "profile", "email"],
      forceRefresh: false,
    };

    // try {
    //   const response: AuthenticationResult = await instance.acquireTokenSilent(silentRequest);
    //   const result = {
    //     token: response.idToken || null,
    //     account: accounts[0]
    //   };
    //   if (onTokenRefreshed) {
    //     onTokenRefreshed(result.token, result.account);
    //   }
    //   return result;
    // }

    try {
      const response: AuthenticationResult = await instance.acquireTokenSilent(
        silentRequest
      );
      const result = {
        token: response.idToken || null,
        account: {
          ...response.account, // Spread all account properties
          idToken: response.idToken, // Ensure idToken is included
        },
      };
      if (onTokenRefreshed) {
        onTokenRefreshed(result.token, result.account);
      }
      return result;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn("Silent token acquisition failed. Interaction required.");
      } else {
        console.error("Error during silent ID token acquisition:", error);
      }
      const result = { token: null, account: null };
      onTokenRefreshed?.(result.token, result.account);
      return result;
    }
  };

  useEffect(() => {
    // Get token immediately on mount with force refresh
    getIdTokenSilently(true);

    // Refresh every minute with force refresh
    intervalRef.current = setInterval(() => {
      getIdTokenSilently(true);
    }, 60 * 60 * 1000); // 4 minute in ms

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [accounts]);

  return getIdTokenSilently;
};

export default useSilentIdTokenRefresh;
